import {
  collection,
  deleteField,
  doc,
  type Firestore,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

export type GastoRutaCompatible = {
  descripcion?: string;
  fecha?: any;
  uid?: string;
  valor?: number | string;
};

export type RutaConGastos = {
  gastosCount?: number | string;
  gastosMigratedAt?: any;
  historialGastos?: GastoRutaCompatible[];
  ultimoGastoAt?: any;
};

export type ActualizacionRutaGasto = {
  gastosCount: number;
  ultimoGastoAt: any;
};

const GASTOS_MIGRATION_BATCH_SIZE = 400;
const LEGACY_GASTO_ID_PREFIX = "legacy-";
const LEGACY_GASTOS_IMPORT_LIMIT = 80;
const GASTOS_FINALIZE_RETRY_DELAYS_MS = [500, 1_500, 3_000];

const normalizarNumero = (value: number | string | null | undefined): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const wait = (delayMs: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
  });

const esErrorDeCuotaExcedida = (error: unknown): boolean => {
  const errorCode = String((error as { code?: unknown } | null)?.code ?? "").toLowerCase();
  const errorMessage = String((error as { message?: unknown } | null)?.message ?? "").toLowerCase();

  return errorCode.includes("resource-exhausted") || errorMessage.includes("quota exceeded");
};

const normalizarFechaGasto = (fecha: any): Date | null => {
  if (fecha instanceof Date && !Number.isNaN(fecha.getTime())) {
    return fecha;
  }

  if (typeof fecha?.toDate === "function") {
    const fechaConvertida = fecha.toDate();

    return fechaConvertida instanceof Date && !Number.isNaN(fechaConvertida.getTime())
      ? fechaConvertida
      : null;
  }

  const fechaConvertida = new Date(fecha);

  return Number.isNaN(fechaConvertida.getTime()) ? null : fechaConvertida;
};

const crearRangoDelDia = (fechaReferencia: Date = new Date()) => {
  const inicioDia = new Date(fechaReferencia);
  inicioDia.setHours(0, 0, 0, 0);

  const finDia = new Date(inicioDia);
  finDia.setDate(finDia.getDate() + 1);

  return {
    finDia,
    inicioDia,
  };
};

const obtenerResumenGastos = (gastos: GastoRutaCompatible[]) => {
  return gastos.reduce(
    (resumen, gasto) => {
      const fechaGasto = normalizarFechaGasto(gasto.fecha);

      if (
        fechaGasto &&
        (!resumen.ultimoGastoAt || fechaGasto.getTime() > resumen.ultimoGastoAt.getTime())
      ) {
        resumen.ultimoGastoAt = fechaGasto;
      }

      return resumen;
    },
    {
      gastosCount: gastos.length,
      ultimoGastoAt: null as Date | null,
    },
  );
};

const normalizarGastoLegacyParaDocumento = (gasto: GastoRutaCompatible): GastoRutaCompatible => ({
  descripcion: gasto.descripcion ?? "Sin descripcion",
  fecha: normalizarFechaGasto(gasto.fecha) ?? new Date(0),
  valor: normalizarNumero(gasto.valor),
});

const obtenerGastosLegacyMasRecientes = (
  gastos: GastoRutaCompatible[],
  limit = LEGACY_GASTOS_IMPORT_LIMIT,
): GastoRutaCompatible[] => {
  if (gastos.length <= limit) {
    return gastos;
  }

  return [...gastos]
    .sort((gastoA, gastoB) => {
      const fechaA = normalizarFechaGasto(gastoA.fecha)?.getTime() ?? 0;
      const fechaB = normalizarFechaGasto(gastoB.fecha)?.getTime() ?? 0;

      return fechaA - fechaB;
    })
    .slice(-limit);
};

export const obtenerHistorialGastosLegacy = (
  rutaData: RutaConGastos | null | undefined,
): GastoRutaCompatible[] =>
  Array.isArray(rutaData?.historialGastos) ? rutaData.historialGastos : [];

export const obtenerHistorialGastosLegacyRecientes = (
  rutaData: RutaConGastos | null | undefined,
  limit = LEGACY_GASTOS_IMPORT_LIMIT,
): GastoRutaCompatible[] =>
  obtenerGastosLegacyMasRecientes(obtenerHistorialGastosLegacy(rutaData), limit);

export const omitirHistorialGastosLegacy = <T extends RutaConGastos>(
  rutaData: T | null | undefined,
): T | Record<string, never> => {
  if (!rutaData) {
    return {};
  }

  const rutaSinLegacy = { ...rutaData };

  delete rutaSinLegacy.historialGastos;

  return rutaSinLegacy;
};

export const tieneHistorialGastosLegacyPendientes = (
  rutaData: RutaConGastos | null | undefined,
): boolean => Object.prototype.hasOwnProperty.call(rutaData ?? {}, "historialGastos");

export const crearGastoRutaRef = (db: Firestore, adminUid: string, rutaUid: string) =>
  doc(collection(db, "admin_users", adminUid, "rutas", rutaUid, "gastos"));

export const crearGastosRutaCollectionRef = (db: Firestore, adminUid: string, rutaUid: string) =>
  collection(db, "admin_users", adminUid, "rutas", rutaUid, "gastos");

export const tieneDocumentosEnGastosRuta = async ({
  adminUid,
  db,
  rutaUid,
}: {
  adminUid: string;
  db: Firestore;
  rutaUid: string;
}): Promise<boolean> => {
  const gastosSnapshot = await getDocs(
    query(crearGastosRutaCollectionRef(db, adminUid, rutaUid), limit(1)),
  );

  return !gastosSnapshot.empty;
};

export const crearGastoRutaLegacyRef = (
  db: Firestore,
  adminUid: string,
  rutaUid: string,
  legacyIndex: number,
) =>
  doc(
    crearGastosRutaCollectionRef(db, adminUid, rutaUid),
    `${LEGACY_GASTO_ID_PREFIX}${legacyIndex}`,
  );

export const construirActualizacionRutaConGasto = (
  rutaData: RutaConGastos | null | undefined,
  fechaGasto: Date = new Date(),
): ActualizacionRutaGasto => {
  const gastosLegacy = obtenerHistorialGastosLegacy(rutaData);
  const gastosCountActual =
    rutaData?.gastosCount !== undefined && rutaData.gastosCount !== null
      ? normalizarNumero(rutaData.gastosCount)
      : gastosLegacy.length;
  const ultimoGastoActual = normalizarFechaGasto(rutaData?.ultimoGastoAt);
  const ultimoGastoAt =
    !ultimoGastoActual || fechaGasto.getTime() >= ultimoGastoActual.getTime()
      ? fechaGasto
      : ultimoGastoActual;

  return {
    gastosCount: gastosCountActual + 1,
    ultimoGastoAt,
  };
};

export const aplicarGastoARutaLocal = <T extends RutaConGastos>(
  rutaData: T,
  fechaGasto: Date = new Date(),
): T => ({
  ...rutaData,
  ...construirActualizacionRutaConGasto(rutaData, fechaGasto),
});

export const calcularGastosDelDia = (
  gastos: GastoRutaCompatible[] | undefined,
  fechaReferencia: Date = new Date(),
): number => {
  if (!gastos?.length) {
    return 0;
  }

  const { finDia, inicioDia } = crearRangoDelDia(fechaReferencia);

  return gastos.reduce((total, gasto) => {
    const fechaGasto = normalizarFechaGasto(gasto.fecha);

    if (!fechaGasto || fechaGasto < inicioDia || fechaGasto >= finDia) {
      return total;
    }

    return total + normalizarNumero(gasto.valor);
  }, 0);
};

export const obtenerGastosDelDiaDeRuta = async ({
  adminUid,
  db,
  fechaReferencia = new Date(),
  rutaUid,
}: {
  adminUid: string;
  db: Firestore;
  fechaReferencia?: Date;
  rutaUid: string;
}): Promise<number> => {
  const { finDia, inicioDia } = crearRangoDelDia(fechaReferencia);
  const gastosSnapshot = await getDocs(
    query(
      crearGastosRutaCollectionRef(db, adminUid, rutaUid),
      where("fecha", ">=", inicioDia),
      where("fecha", "<", finDia),
    ),
  );

  return gastosSnapshot.docs.reduce((total, gastoDoc) => {
    const gastoData = gastoDoc.data() as GastoRutaCompatible;

    return total + normalizarNumero(gastoData.valor);
  }, 0);
};

export const migrarHistorialGastosLegacyDeRuta = async ({
  adminUid,
  db,
  rutaData,
  rutaUid,
}: {
  adminUid: string;
  db: Firestore;
  rutaData: RutaConGastos | null | undefined;
  rutaUid: string;
}): Promise<RutaConGastos> => {
  if (!tieneHistorialGastosLegacyPendientes(rutaData)) {
    return rutaData ?? {};
  }

  const legacyGastos = obtenerHistorialGastosLegacyRecientes(rutaData);
  const gastosCollectionRef = crearGastosRutaCollectionRef(db, adminUid, rutaUid);
  const gastosExistentesSnapshot = await getDocs(gastosCollectionRef);
  const gastosLegacyIdsExistentes = new Set(gastosExistentesSnapshot.docs.map((gastoDoc) => gastoDoc.id));

  for (
    let startIndex = 0;
    startIndex < legacyGastos.length;
    startIndex += GASTOS_MIGRATION_BATCH_SIZE
  ) {
    const batch = writeBatch(db);
    const gastosChunk = legacyGastos.slice(startIndex, startIndex + GASTOS_MIGRATION_BATCH_SIZE);
    let hasPendingWrites = false;

    gastosChunk.forEach((gasto, chunkIndex) => {
      const legacyIndex = startIndex + chunkIndex;
      const legacyGastoId = `${LEGACY_GASTO_ID_PREFIX}${legacyIndex}`;

      if (gastosLegacyIdsExistentes.has(legacyGastoId)) {
        return;
      }

      batch.set(
        doc(gastosCollectionRef, legacyGastoId),
        normalizarGastoLegacyParaDocumento(gasto),
      );
      hasPendingWrites = true;
    });

    if (hasPendingWrites) {
      await batch.commit();
    }
  }

  const gastosSnapshot = await getDocs(gastosCollectionRef);
  const gastosActuales = gastosSnapshot.docs.map(
    (gastoDoc) => gastoDoc.data() as GastoRutaCompatible,
  );
  const resumenActualizado = obtenerResumenGastos(gastosActuales);
  const gastosMigratedAt = new Date();
  const rutaRef = doc(db, "admin_users", adminUid, "rutas", rutaUid);
  const ultimoGastoAt =
    resumenActualizado.ultimoGastoAt ?? normalizarFechaGasto(rutaData?.ultimoGastoAt);
  const rutaMigradaUpdates = {
    gastosCount: resumenActualizado.gastosCount,
    gastosMigratedAt,
    historialGastos: deleteField(),
    ultimoGastoAt: ultimoGastoAt ?? null,
  };

  for (let attemptIndex = 0; attemptIndex <= GASTOS_FINALIZE_RETRY_DELAYS_MS.length; attemptIndex++) {
    try {
      await updateDoc(rutaRef, rutaMigradaUpdates);
      break;
    } catch (error) {
      const isLastAttempt = attemptIndex === GASTOS_FINALIZE_RETRY_DELAYS_MS.length;

      if (!esErrorDeCuotaExcedida(error) || isLastAttempt) {
        throw error;
      }

      await wait(GASTOS_FINALIZE_RETRY_DELAYS_MS[attemptIndex]);
    }
  }

  return {
    ...omitirHistorialGastosLegacy((rutaData ?? {}) as RutaConGastos),
    gastosCount: resumenActualizado.gastosCount,
    gastosMigratedAt,
    ultimoGastoAt: ultimoGastoAt ?? null,
  };
};
